import { useMemo, useState } from "react";
import { FaPlug, FaArrowRight, FaCopy, FaCheck } from "react-icons/fa6";

// components
import SelectBox from "../form-inputs/select-box.component";
import type { SelectOption } from "../form-inputs/select-box.component";
import EmptyRecord from "../empty-record/empty-record.component";
import InlineNotice from "../inline-notice/inline-notice.component";
import FormError from "../form-error/form-error.component";
import TabNav from "../tab-nav/tab-nav.component";
import type { TabNavItem } from "../tab-nav/tab-nav.component";
import DialogModal from "../dialog-modal/dialog-modal.component";
import { Button } from "../ui/button";

// toast — clipboard copy is a micro-confirmation, the one case this codebase
// keeps as a toast (Material Design's own textbook case for it)
import showToastMessage from "../../global/showToastMessage";

// queries
import {
	useAvailableApps,
	useConnectedApps,
	useActiveExternalApp,
	useConnectApplication,
	useUpdateAppConnection,
	useRegenerateAppCode,
	useCompanyDetails,
} from "../../queries/company.queries";

import "./company-app-setting.style.scss";

const SUB_TABS: TabNavItem[] = [
	{ key: "manage", label: "Manage Connection" },
	{ key: "info", label: "How It Works" },
	{ key: "actions", label: "Available Actions" },
];

interface ModelInfo {
	title: string;
	shortLabel: string;
	description: string;
	model: "A" | "B";
}

function getModelInfo(billModel: "A" | "B" | undefined): ModelInfo | null {
	if (billModel === "B") {
		return {
			title: "BillMe-Managed Billing",
			shortLabel: "Bills created in BillMe",
			description:
				"Bills are created in BillMe and automatically synced to the external app after IRD submission. Your existing billing workflow remains unchanged.",
			model: "B",
		};
	}
	if (billModel === "A") {
		return {
			title: "Externally-Managed Billing",
			shortLabel: "Bills created externally",
			description:
				"Bills are created and managed by the external app. Once connected, bill creation in BillMe will be disabled. Bills will be pushed into BillMe from the external app.",
			model: "A",
		};
	}
	return null;
}

function CompanyAppSetting() {
	const { data: companyData } = useCompanyDetails();
	const company_info_id = companyData?.company?.company_info_id;

	const { data: availableApps } = useAvailableApps();
	const { data: connectedApps } = useConnectedApps();
	const { systemType: activeSystemType } = useActiveExternalApp();
	const connectApplicationMutation = useConnectApplication();
	const updateAppConnectionMutation = useUpdateAppConnection();
	const regenerateAppCodeMutation = useRegenerateAppCode();

	const isLoading =
		connectApplicationMutation.isPending ||
		updateAppConnectionMutation.isPending ||
		regenerateAppCodeMutation.isPending;

	const [activeTab, setActiveTab] = useState("manage");
	const [selectedApp, setSelectedApp] = useState("");
	const [confirmDisconnect, setConfirmDisconnect] = useState<string | null>(null);
	const [regenWarning, setRegenWarning] = useState<string | null>(null);
	const [copiedCode, setCopiedCode] = useState<string | null>(null);

	// currently-active connections — at most one is expected (one-at-a-time guard below)
	const activeConnections = useMemo(
		() => (connectedApps ?? []).filter((app) => app.system_access),
		[connectedApps],
	);
	const isBlockedByExistingConnection =
		!!activeSystemType && !!selectedApp && selectedApp !== activeSystemType;

	// dropdown only offers apps that aren't already actively connected, and are
	// globally enabled (SystemKey.system_access — connectToApplication 400s otherwise)
	const appOptions = useMemo<SelectOption[]>(() => {
		const activeTypes = new Set(activeConnections.map((app) => app.system_type));
		return (availableApps ?? [])
			.filter((app) => app.system_access && !activeTypes.has(app.system_type))
			.map((app) => ({ id: app.system_type, value: app.system_type.toUpperCase() }));
	}, [availableApps, activeConnections]);

	const selectedConnectedRecord = useMemo(
		() => (connectedApps ?? []).find((app) => app.system_type === selectedApp) ?? null,
		[connectedApps, selectedApp],
	);
	const isReconnectFlow = !!selectedConnectedRecord && !selectedConnectedRecord.system_access;

	const selectedAvailableApp = useMemo(
		() => (availableApps ?? []).find((app) => app.system_type === selectedApp) ?? null,
		[availableApps, selectedApp],
	);
	const selectedModelInfo = getModelInfo(selectedAvailableApp?.bill_model);

	const handleCopyCode = (systemType: string, code: string) => {
		navigator.clipboard.writeText(code);
		setCopiedCode(systemType);
		showToastMessage("Code Copied", "S");
		setTimeout(() => setCopiedCode(null), 2000);
	};

	const handleConnect = async () => {
		if (!company_info_id || !selectedApp) return;
		await connectApplicationMutation.mutateAsync({
			company_info_id,
			system_type: selectedApp,
			system_access: true,
		});
		setSelectedApp("");
	};

	const handleReconnect = async () => {
		if (!selectedConnectedRecord?.company_system_code_id) return;
		await updateAppConnectionMutation.mutateAsync({
			company_info_id: selectedConnectedRecord.company_info_id,
			company_system_code_id: selectedConnectedRecord.company_system_code_id,
			system_access: true,
			system_type: selectedApp,
		});
		setSelectedApp("");
	};

	const handleDisconnect = async (systemType: string) => {
		const record = (connectedApps ?? []).find((app) => app.system_type === systemType);
		setConfirmDisconnect(null);
		if (!record) return;
		await updateAppConnectionMutation.mutateAsync({
			company_info_id: record.company_info_id,
			company_system_code_id: record.company_system_code_id,
			system_access: false,
			system_type: systemType,
		});
	};

	const handleRegenerateCode = async (systemType: string) => {
		await regenerateAppCodeMutation.mutateAsync({ system_type: systemType });
		setRegenWarning(systemType);
		setTimeout(() => setRegenWarning(null), 5000);
	};

	function renderAccessAction() {
		if (isReconnectFlow) {
			return (
				<>
					<p className="regen-note">
						This app was previously connected. Reconnecting will re-enable
						bill sync using the existing code — no changes needed on the
						external app side.
					</p>
					<button
						type="button"
						className="connect-app-btn"
						onClick={handleReconnect}
						disabled={isLoading}
					>
						Reconnect
					</button>
				</>
			);
		}
		return (
			<>
				<p className="regen-note">
					Connecting will generate a system code automatically. Share it
					with your external app team to establish the connection.
				</p>
				<button
					type="button"
					className="connect-app-btn"
					onClick={handleConnect}
					disabled={isLoading}
				>
					Connect
				</button>
			</>
		);
	}

	function renderHowItWorks() {
		return (
			<div className="how-it-works-container">
				<p className="how-it-works-intro">
					BillMe supports connecting third-party applications to your billing
					workflow. Depending on the app, one of two billing models will be
					used:
				</p>

				<div className="model-flow-card model-b">
					<div className="model-flow-header">
						<div className="model-flow-badge">Model B</div>
						<h5 className="model-flow-title">BillMe-Managed Billing</h5>
					</div>
					<p className="model-flow-subtitle">
						BillMe creates bills. External app receives them.
					</p>
					<div className="model-flow-steps">
						<div className="flow-step">
							<div className="flow-step-num">1</div>
							<div className="flow-step-text">Create bills in BillMe as normal</div>
						</div>
						<FaArrowRight className="flow-arrow" />
						<div className="flow-step">
							<div className="flow-step-num">2</div>
							<div className="flow-step-text">Bills are submitted to IRD</div>
						</div>
						<FaArrowRight className="flow-arrow" />
						<div className="flow-step">
							<div className="flow-step-num">3</div>
							<div className="flow-step-text">
								Bills automatically sync to the external app
							</div>
						</div>
						<FaArrowRight className="flow-arrow" />
						<div className="flow-step">
							<div className="flow-step-num">4</div>
							<div className="flow-step-text">External app receives bill data</div>
						</div>
					</div>
				</div>

				<div className="model-flow-card model-a">
					<div className="model-flow-header">
						<div className="model-flow-badge">Model A</div>
						<h5 className="model-flow-title">Externally-Managed Billing</h5>
					</div>
					<p className="model-flow-subtitle">
						External app creates bills. BillMe receives and submits to IRD.
					</p>
					<div className="model-flow-steps">
						<div className="flow-step">
							<div className="flow-step-num">1</div>
							<div className="flow-step-text">Bills are created in the external app</div>
						</div>
						<FaArrowRight className="flow-arrow" />
						<div className="flow-step">
							<div className="flow-step-num">2</div>
							<div className="flow-step-text">External app pushes bills into BillMe</div>
						</div>
						<FaArrowRight className="flow-arrow" />
						<div className="flow-step">
							<div className="flow-step-num">3</div>
							<div className="flow-step-text">BillMe submits bills to IRD</div>
						</div>
						<FaArrowRight className="flow-arrow" />
						<div className="flow-step">
							<div className="flow-step-num">4</div>
							<div className="flow-step-text">Bill creation in BillMe is disabled</div>
						</div>
					</div>
				</div>

				<div className="how-it-works-note">
					<h5>Connecting &amp; Disconnecting</h5>
					<ul>
						<li>Only one external app can be connected at a time</li>
						<li>To switch apps, disconnect the current one first</li>
						<li>Disconnecting stops bill sync — you can reconnect anytime</li>
						<li>After reconnecting, bill sync resumes automatically</li>
					</ul>
				</div>
			</div>
		);
	}

	function renderAvailableActions() {
		const actions = [
			{
				label: "Connect",
				btnClass: "connect-app-btn",
				title: "First-time connection",
				desc: "Establishes a new link between BillMe and the external app. A system code is generated automatically — share it with your external app team.",
			},
			{
				label: "Reconnect",
				btnClass: "connect-app-btn",
				title: "Re-enable a disconnected app",
				desc: "Restores bill sync using the existing system code. The external app team does not need to make any changes.",
			},
			{
				label: "Regenerate Code",
				btnClass: "regen-code-btn",
				title: "Generate a new system code",
				desc: "The old code is immediately invalidated. Share the new code with your external app team so they can update their connection. Use this only when necessary (e.g. security concern or lost code).",
			},
			{
				label: "Disconnect",
				btnClass: "connected-app-disconnect-btn",
				title: "Pause the connection",
				desc: "Stops bill sync with the external app. The system code is preserved — you can reconnect later without needing a new code.",
			},
		];
		return (
			<div className="available-actions-container">
				<p className="available-actions-intro">
					Each action on the Manage Connection tab is described below.
				</p>
				<div className="action-guide-list">
					{actions.map((action) => (
						<div className="action-guide-item" key={action.label}>
							<div className="action-guide-btn-wrap">
								<button type="button" className={`${action.btnClass} action-guide-btn`} disabled>
									{action.label}
								</button>
							</div>
							<div className="action-guide-desc">
								<b>{action.title}.</b> {action.desc}
							</div>
						</div>
					))}
				</div>
			</div>
		);
	}

	return (
		<div className="company-setting-container">
			<TabNav tabs={SUB_TABS} activeKey={activeTab} onChange={setActiveTab} />

			{activeTab === "manage" ? (
				<div className="settings-wrapper">
					<div className="setting-container">
						<div className="setting-content">
							<h4 className="setting-title">Connected External Applications</h4>
							<div className="setting-action connected-wrapper">
								{activeConnections.length > 0 ? (
									activeConnections.map((app) => {
										const modelInfo = getModelInfo(app.system_key?.bill_model);
										return (
											<div className="connected-container" key={app.company_system_code_id}>
												<div className="connected-app-header">
													<div className="connected-app-icon">
														<FaPlug />
													</div>
													<div className="connected-app-name">
														{app.system_type.toUpperCase()}
													</div>
												</div>
												<div className="connected-app-status">
													<span className="status-dot active" />
													Connected
												</div>
												{modelInfo && (
													<div
														className={`connected-app-model-info model-${modelInfo.model.toLowerCase()}`}
													>
														<div className="model-info-label">{modelInfo.shortLabel}</div>
														<div className="model-info-desc">{modelInfo.description}</div>
													</div>
												)}
												<div className="connected-app-code-wrapper">
													<div className="code-label">System Code</div>
													<div className="code-display-row">
														<div className="code-value">{app.system_code}</div>
														<button
															type="button"
															className={`copy-code-btn ${copiedCode === app.system_type ? "copied" : ""}`}
															onClick={() => handleCopyCode(app.system_type, app.system_code)}
															title="Copy to clipboard"
														>
															{copiedCode === app.system_type ? <FaCheck /> : <FaCopy />}
														</button>
													</div>
													{regenWarning === app.system_type && (
														<div className="regen-warning">
															New code generated. Share this with your external app team —
															the old code is now invalid.
														</div>
													)}
												</div>
												<div className="connected-tile-actions">
													<button
														type="button"
														className="regen-code-btn"
														onClick={() => handleRegenerateCode(app.system_type)}
														disabled={isLoading}
													>
														Regenerate Code
													</button>
													<div className="connected-app-disconnect-wrapper">
														<button
															type="button"
															className="connected-app-disconnect-btn"
															onClick={() => setConfirmDisconnect(app.system_type)}
															disabled={isLoading}
														>
															Disconnect
														</button>
														<span className="disconnect-tooltip">
															Disconnecting will stop bill sync with this application. You
															can reconnect later using a new system code.
														</span>
													</div>
												</div>
											</div>
										);
									})
								) : (
									<EmptyRecord caption="No external applications connected" />
								)}
							</div>
						</div>
					</div>

					<div className="setting-container">
						<div className="setting-content">
							<h4 className="setting-title">Change External Applications Access</h4>

							<div className="setting-action">
								<div className="input-item">
									<p className="input-label">Choose application</p>
									<SelectBox
										name="app"
										options={appOptions}
										value={selectedApp}
										onChange={(e) => setSelectedApp(e.target.value)}
										disabled={isLoading}
									/>
								</div>
							</div>

							{selectedApp && selectedModelInfo && (
								<div className={`app-model-info-card model-${selectedModelInfo.model.toLowerCase()}`}>
									<div className="model-info-card-title">{selectedModelInfo.title}</div>
									<p className="model-info-card-description">{selectedModelInfo.description}</p>
								</div>
							)}

							{selectedApp && (
								<div className="setting-action">
									{isBlockedByExistingConnection ? (
										<div className="connection-blocked-message">
											You are already connected to{" "}
											<b className="app-name-caps">{activeSystemType}</b>.
											Disconnect it before connecting a new application.
										</div>
									) : (
										renderAccessAction()
									)}
								</div>
							)}
						</div>
					</div>
				</div>
			) : activeTab === "info" ? (
				renderHowItWorks()
			) : (
				renderAvailableActions()
			)}

			<FormError
				isError={
					connectApplicationMutation.isError ||
					updateAppConnectionMutation.isError ||
					regenerateAppCodeMutation.isError
				}
				error={
					connectApplicationMutation.error ||
					updateAppConnectionMutation.error ||
					regenerateAppCodeMutation.error
				}
				fallback="Failed to update application connection."
			/>

			<DialogModal
				open={!!confirmDisconnect}
				onOpenChange={(open) => !open && setConfirmDisconnect(null)}
				title="Disconnect Application"
				description={
					confirmDisconnect
						? `Are you sure you want to disconnect ${confirmDisconnect.toUpperCase()}? Bill sync with this application will stop.`
						: undefined
				}
				footer={
					<>
						<Button variant="outline" onClick={() => setConfirmDisconnect(null)} disabled={isLoading}>
							Cancel
						</Button>
						<Button
							variant="destructive"
							onClick={() => confirmDisconnect && handleDisconnect(confirmDisconnect)}
							disabled={isLoading}
						>
							Yes, Disconnect
						</Button>
					</>
				}
			>
				<InlineNotice
					message="The system code is preserved — you can reconnect later without generating a new one."
					type="info"
				/>
			</DialogModal>
		</div>
	);
}

export default CompanyAppSetting;
